import type {
  PlanningLevelTraceStep,
  ResolvedPlanningLevels,
} from '@erp/shared-types';
import { T, useGT } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import type { ForecastDetailPolicy } from '@erp/rpc';
import { effectivePolicySourceLabel } from './panelFormat';

type Translate = ReturnType<typeof useGT>;

function stepLabel(
  kind: PlanningLevelTraceStep['kind'],
  gt: Translate,
): string {
  switch (kind) {
    case 'hard_min':
      return gt('Hard min', { context: 'policy override field' });
    case 'hard_max':
      return gt('Hard max', { context: 'policy override field' });
    case 'fixed':
      return gt('Fixed min/max', { context: 'planning level rule step' });
    case 'higher_of_calculated_and_floor':
      return gt('Higher of calculated and floor', {
        context: 'planning level rule step',
      });
    case 'calculated':
      return gt('Calculated', { context: 'planning level rule step' });
  }
}

function statusLabel(status: PlanningLevelTraceStep['status'], gt: Translate) {
  switch (status) {
    case 'applied':
      return gt('Applied', { context: 'planning level trace status' });
    case 'lost':
      return gt('Lost', { context: 'planning level trace status' });
    case 'not_set':
      return gt('Not set', { context: 'planning level trace status' });
    case 'skipped':
      return gt('Skipped', { context: 'planning level trace status' });
    case 'clamped':
      return gt('Clamped', { context: 'planning level trace status' });
  }
}

function quantity(value: number | null) {
  return value === null
    ? '—'
    : value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/** Values come from the resolver; this only places their markers on a scale. */
function LevelNumberLine({
  levels,
  calculated,
  floor,
  calculatedLabel,
}: {
  levels: ResolvedPlanningLevels;
  calculated: number | null;
  floor: number | null;
  calculatedLabel: string;
}) {
  const gt = useGT();
  const markers = [
    {
      label: calculatedLabel,
      value: calculated,
      symbol: '●',
      className: 'text-muted-foreground',
    },
    {
      label: gt('Min floor', { context: 'planning level candidate' }),
      value: floor,
      symbol: '△',
      className: 'text-muted-foreground',
    },
    {
      label: gt('Protection', { context: 'resolved planning level' }),
      value: levels.protection.value,
      symbol: '◆',
      className: 'text-primary',
    },
    {
      label: gt('Order up to', { context: 'resolved planning level' }),
      value: levels.orderUpTo.value,
      symbol: '■',
      className: 'text-foreground',
    },
  ];
  const values = markers.flatMap(({ value }) =>
    value === null ? [] : [value],
  );
  const { hardMin, hardMax } = hardStopBounds(levels);
  values.push(
    ...[hardMin, hardMax].filter((value): value is number => value !== null),
  );
  const start = Math.min(0, ...values);
  const end = Math.max(1, ...values);
  const x = (value: number) => 8 + ((value - start) / (end - start)) * 304;
  return (
    <div className="min-w-0">
      <svg viewBox="0 0 320 80" className="block w-full" aria-hidden="true">
        <HardStopBand levels={levels} start={start} end={end} x={x} />
        <path
          d="M8 68 H312 M8 64 V72 M312 64 V72"
          fill="none"
          className="stroke-border"
        />
        {markers.map(({ label, value, symbol, className }, index) =>
          value === null ? null : (
            <g key={label} className={className}>
              <line
                x1={x(value)}
                x2={x(value)}
                y1={12 + index * 15}
                y2={68}
                stroke="currentColor"
                strokeDasharray="2 3"
              />
              <text
                x={x(value)}
                y={12 + index * 15}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="currentColor"
                fontSize="13"
              >
                {symbol}
              </text>
            </g>
          ),
        )}
      </svg>
      <div
        className="flex justify-between text-muted-foreground tabular-nums"
        aria-hidden="true"
      >
        <span>{quantity(start)}</span>
        <span>{quantity(end)}</span>
      </div>
      <dl className="mt-1 grid grid-cols-1 gap-x-3 gap-y-1 sm:grid-cols-2">
        {markers.map(({ label, value, symbol, className }) => (
          <div
            key={label}
            className="flex min-w-0 flex-wrap justify-between gap-x-2"
          >
            <dt className="min-w-0 break-words">
              <span className={className} aria-hidden="true">
                {symbol}
              </span>{' '}
              {label}
            </dt>
            <dd className="tabular-nums">{quantity(value)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function PlanningLevelsTrace({
  levels,
  policy,
}: {
  levels: ResolvedPlanningLevels;
  policy: ForecastDetailPolicy;
}) {
  const gt = useGT();
  const source = policy.effectiveLevelSources[0];
  const calculatedLabel = source
    ? effectivePolicySourceLabel(source, gt)
    : stepLabel('calculated', gt);
  const calculated =
    levels.trace.find(
      (step) => step.level === 'protection' && step.kind === 'calculated',
    )?.value ?? null;
  const protectionSource =
    levels.protection.setBy === 'higher_of_calculated_and_floor'
      ? policy.minFloorAppliedCount > 0
        ? gt('Min floor', { context: 'planning level candidate' })
        : calculatedLabel
      : stepLabel(levels.protection.setBy, gt);
  return (
    <div className="mt-3 min-w-0 space-y-2 border-t pt-2 text-xs">
      <dl className="space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt>
            <T context="resolved planning level">Protection</T>
          </dt>
          <dd className="font-semibold tabular-nums">
            {quantity(levels.protection.value)}
          </dd>
          <dd className="min-w-0 text-muted-foreground break-words">
            ← {protectionSource}
          </dd>
        </div>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <dt>
            <T context="resolved planning level">Order up to</T>
          </dt>
          <dd className="font-semibold tabular-nums">
            {quantity(levels.orderUpTo.value)}
          </dd>
          <dd className="min-w-0 text-muted-foreground break-words">
            ← {stepLabel(levels.orderUpTo.setBy, gt)}
          </dd>
        </div>
      </dl>
      <LevelNumberLine
        levels={levels}
        calculated={calculated}
        floor={policy.minFloor}
        calculatedLabel={calculatedLabel}
      />
      <HardStopsRow levels={levels} />
      <h4 className="font-medium">
        <T context="planning level trace heading">How it was decided</T>
      </h4>
      <ol className="space-y-2">
        {levels.trace.map((step, index) => (
          <li
            key={`${step.level}-${step.kind}-${step.status}`}
            className="min-w-0 space-y-0.5"
          >
            <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
              <span className="min-w-0 flex-1 break-words">
                {index + 1}.{' '}
                {step.level === 'protection'
                  ? gt('Protection', { context: 'resolved planning level' })
                  : gt('Order up to', {
                      context: 'resolved planning level',
                    })}{' '}
                · {stepLabel(step.kind, gt)}
              </span>
              <Badge
                variant={step.status === 'applied' ? 'secondary' : 'outline'}
              >
                {statusLabel(step.status, gt)}
              </Badge>
            </div>
            <p className="text-muted-foreground tabular-nums break-words">
              {step.kind === 'higher_of_calculated_and_floor' &&
              step.status !== 'clamped'
                ? gt('{calculated} vs floor {floor} → {value}', {
                    context: 'planning level trace comparison',
                    calculated: quantity(calculated),
                    floor: quantity(policy.minFloor),
                    value: quantity(step.value),
                  })
                : step.status === 'clamped'
                  ? gt('{previous} → {value}', {
                      context: 'planning level raised to protection',
                      previous: quantity(step.previousValue),
                      value: quantity(step.value),
                    })
                  : quantity(step.value)}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function HardStopsRow({ levels }: { levels: ResolvedPlanningLevels }) {
  const gt = useGT();
  const stops = levels.hardStops;
  const clamped = levels.trace.some(
    (step) =>
      (step.kind === 'hard_min' || step.kind === 'hard_max') &&
      step.status === 'clamped',
  );
  return (
    <div
      className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1"
      data-testid="hard-stops-row"
    >
      <span className="font-medium">
        <T context="planning level trace heading">Hard stops</T>
      </span>
      <span className="tabular-nums break-words">
        {gt('Min {min} · Max {max}', {
          context: 'planning hard stop bounds',
          min: quantity(stops?.min ?? null),
          max: quantity(stops?.max ?? null),
        })}
      </span>
      <Badge variant="outline">
        {stops
          ? clamped
            ? gt('Clamped', { context: 'planning level trace status' })
            : gt('Within', { context: 'planning hard stop status' })
          : gt('Not set', { context: 'planning level trace status' })}
      </Badge>
      {stops?.conflict ? (
        <span className="text-destructive break-words">
          <T context="conflicting planning hard stops">
            Conflicting hard stops; hard max wins.
          </T>
        </span>
      ) : null}
    </div>
  );
}

function hardStopBounds(levels: ResolvedPlanningLevels) {
  return {
    hardMin: levels.hardStops?.min ?? null,
    hardMax: levels.hardStops?.max ?? null,
  };
}

function HardStopBand({
  levels,
  start,
  end,
  x,
}: {
  levels: ResolvedPlanningLevels;
  start: number;
  end: number;
  x: (value: number) => number;
}) {
  if (!levels.hardStops) return null;
  const left = x(levels.hardStops.min ?? start);
  const right = x(levels.hardStops.max ?? end);
  return (
    <rect
      x={Math.min(left, right)}
      width={Math.abs(right - left)}
      y={4}
      height={68}
      className="fill-primary/10 stroke-primary/40"
      data-testid="hard-stop-band"
    />
  );
}
